import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Http from "../../../Common/Http";
import { CUSTOM_PRESET, DEFAULT_PRESET, PRESETS, periodFromParams, presetRange, withBound } from "./periods";

/**
 * Reporting period, mirrored into the URL so a dashboard view can be
 * bookmarked or pasted into a message and come back showing the same numbers.
 */
export const useDashboardPeriod = ({ presets = PRESETS, defaultPreset = DEFAULT_PRESET } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(() => periodFromParams(searchParams, new Date(), presets, defaultPreset));

  useEffect(() => {
    // All time has no start date; keep "from" out of the URL rather than writing "null".
    const params = { period: period.preset, from: period.range.from, to: period.range.to };
    if (!params.from) delete params.from;
    setSearchParams(params, { replace: true });
  }, [period, setSearchParams]);

  const selectPreset = useCallback(
    (key) => setPeriod({ preset: key, range: presetRange(key, new Date(), presets, defaultPreset) }),
    [presets, defaultPreset]
  );

  const setBound = useCallback((field, value) => {
    setPeriod((current) => {
      const range = withBound(current.range, field, value);
      return range ? { preset: CUSTOM_PRESET, range } : current;
    });
  }, []);

  const setRange = useCallback((range) => setPeriod({ preset: CUSTOM_PRESET, range }), []);

  return { ...period, selectPreset, setBound, setRange };
};

/**
 * Loads a dashboard endpoint for a range. Every request aborts the one before
 * it, so a slow response can never overwrite a newer period. After the first
 * load, refetches keep the previous data on screen (`refreshing`).
 */
export const useDashboardData = (url, { from, to }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const controllerRef = useRef(null);

  const load = useCallback(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setFetching(true);
    setError(null);

    Http.get(url, { params: { from, to }, signal: controller.signal })
      .then((res) => {
        setData(res.data);
        setUpdatedAt(new Date());
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error("Dashboard load failed:", err);
        setError(err?.response?.data?.message || "Could not load dashboard data. Please try again.");
      })
      .finally(() => {
        if (controllerRef.current === controller) setFetching(false);
      });
  }, [url, from, to]);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  return {
    data,
    error,
    loading: fetching && !data,
    refreshing: fetching && Boolean(data),
    updatedAt,
    reload: load,
  };
};

export const useAdminDashboard = (range) => useDashboardData("/dashboard/admin", range);
